import { parseUnits } from 'viem';
import { supportedTokens, type SupportedNetworkIds } from '@/config';
import { truncateAddress } from '@/lib/format/address';
import { displaySymbol } from '@/lib/token-symbol';
import { formatDecimal } from '@/lib/format/number/decimal';
import { formatCompactUnits } from '@/lib/format/token-amount/compact-units';
import { formatRoundedUnits } from '@/lib/format/token-amount/rounded-units';
import { formatUnitsFixed } from '@/lib/format/token-amount/units-fixed';
import { formatDecimalDollars } from '@/lib/format/usd/cents';
import { formatCompactMoney } from '@/lib/format/usd/compact-money';
import { formatUsdFigure } from '@/lib/format/usd/usd-figure';

import type { InvestorHolding, InvestorHoldingsResponse } from '../types/api';
import { humanizeSlug } from './format';

/**
 * How a single holding is described, in one place.
 *
 * Both the Positions card and the full positions table render the same
 * rows, and a holding that reads "0.0001 units" in one and "—" in the other
 * is a bug the reader has no way to resolve. These live here so there is one
 * answer per shape rather than two implementations that drift.
 */

export const KIND_LABELS: Record<InvestorHolding['kind'], string> = {
  rowa: 'ROWA vault',
  external: 'Wallet asset',
  cash: 'Cash'
};

const PRICE_SOURCE_LABELS: Record<string, string> = {
  nav: 'Vault NAV',
  'market-data': 'Market data',
  alchemy: 'Alchemy'
};

/** Known tiers get a plain name; unknown slugs are humanized, never raw. */
const LIQUIDITY_TIER_LABELS: Record<string, string> = {
  deep: 'Deep liquidity',
  standard: 'Standard liquidity',
  thin: 'Thin liquidity',
  tier_1: 'Tier 1 liquidity',
  tier_2: 'Tier 2 liquidity',
  tier_3: 'Tier 3 liquidity'
};

export function tierLabel(tier: string): string {
  return LIQUIDITY_TIER_LABELS[tier] ?? humanizeSlug(tier);
}

/** One wording for each reason the wallet's own tokens are not counted. */
export const WALLET_TRACKING_OFF = 'Wallet tracking is off on this network';
export const WALLET_TOKENS_UNREADABLE =
  "We can't read your wallet tokens right now";

/**
 * Why the wallet's own tokens are not in a /holdings response, when they
 * are not: the scan is switched off on this network, or out right now.
 * Cash rows ARE wallet tokens, so either way the served `cashValueUsd`
 * "0.00" is the absence of a reading, not an empty wallet (QA 2026-10-04:
 * "$0" idle capital beside 2,631.62 USDC in the wallet). Idle capital is
 * then unknown, and is neither printed nor scored. Null when the tokens
 * are counted, or when the coverage does not say.
 */
export function walletTokensUntracked(
  coverage: { externalIncluded?: boolean; externalStatus?: string } | undefined
): 'disabled' | 'unavailable' | null {
  if (coverage?.externalStatus === 'disabled') return 'disabled';
  if (coverage?.externalIncluded === false) return 'unavailable';
  return null;
}

/**
 * A holding's ticker: a token the app configures keeps its configured
 * name (Sepolia's tUSDC answers "USDC" on chain); any other token is
 * named by its own symbol where the backend read one.
 */
export function holdingSymbol(holding: InvestorHolding): string | null {
  return displaySymbol(holding.tokenSymbol, holding.symbol, holding.address);
}

export function holdingDisplayName(holding: InvestorHolding): string {
  return (
    holding.name ??
    holdingSymbol(holding) ??
    (holding.address ? truncateAddress(holding.address) : 'Native asset')
  );
}

/**
 * A holding's id on its own. A holding without an address has no stable
 * identity to key on, so its symbol and kind are folded in; uniqueness
 * across a whole response is `holdingRowIds`' job.
 */
function holdingRowId(holding: InvestorHolding): string {
  return holding.address != null
    ? `${holding.chainId}:${holding.address}`
    : `${holding.chainId}:native:${holding.kind}:${holding.symbol ?? holding.name ?? ''}`;
}

/**
 * One unique id per holding in a response, for React keys, donut segments
 * and the row a detail drawer belongs to. Every holding without an address
 * used to share `chainId:native`, so two of them collided on all three. A
 * repeat of the same id (which the contract does not serve, but a key must
 * never depend on that) takes a numbered suffix.
 */
export function holdingRowIds(
  holdings: readonly InvestorHolding[]
): Map<InvestorHolding, string> {
  const seen = new Map<string, number>();
  const ids = new Map<InvestorHolding, string>();
  for (const holding of holdings) {
    const base = holdingRowId(holding);
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    ids.set(holding, count === 1 ? base : `${base}#${count}`);
  }
  return ids;
}

/**
 * The asset registry by lowercased address, as much of it as naming a
 * currency needs. Loaded by the view (`useHoldingCurrencies`) and passed
 * down; these helpers never fetch.
 */
export type CurrencyRegistry = ReadonlyMap<
  string,
  { symbol: string | null; tokenSymbol?: string | null }
>;

interface CurrencyUnit {
  /** What to print after an amount. Never a raw address. */
  symbol: string;
  /** A dollar stablecoin for the chain: its amounts are dollar amounts. */
  isDollar: boolean;
}

type HoldingsFx = InvestorHoldingsResponse['fx'];

/**
 * How far a dollar stablecoin's served rate may sit from $1 before its face
 * value stops being a fair dollar figure. Inside the band the coin is a
 * dollar (rate noise like 1.0001 is not applied); outside it, the served
 * rate converts it and it prints in its own unit, so a depeg shows.
 */
const DOLLAR_PEG_TOLERANCE = 0.01;

/** The chain's dollar stablecoin at this address, if it is one. */
function findDollarCoin(chainId: number, address: string) {
  const needle = address.trim().toLowerCase();
  return supportedTokens[chainId as SupportedNetworkIds]?.tokens.find(
    (token) => token.tokenAddress.toLowerCase() === needle
  );
}

/** The served rate for a currency address, matched in lowercase. */
function servedRate(currency: string, fx: HoldingsFx) {
  const needle = currency.trim().toLowerCase();
  return fx?.rates?.find(
    (rate) => rate.currency?.trim().toLowerCase() === needle
  );
}

/**
 * What a strategy currency address is, for display and for dollar sums.
 *
 * `nativeCurrency` and `fx.rates[].currency` are lowercase contract
 * addresses on the wire. Two lookups, in order, the same rule the
 * strategist's book uses (docs/features/vault-management.md):
 * 1. The chain's `supportedTokens` set, by address. Every entry there is a
 *    dollar stablecoin, so a hit names the currency AND licenses a `$`,
 *    unless this response serves it a rate more than a percent off $1: a
 *    depegged coin is converted at that rate and printed in its own unit.
 * 2. The asset registry, by address. A hit names the currency, and is never
 *    a dollar claim.
 * Anything else is shown as a shortened address. The raw one is never
 * printed as a unit.
 */
function currencyUnit(
  chainId: number,
  address: string,
  registry: CurrencyRegistry | undefined,
  fx: HoldingsFx
): CurrencyUnit {
  const dollarCoin = findDollarCoin(chainId, address);
  if (dollarCoin) {
    const rate = finiteNumber(servedRate(address, fx)?.priceUsd);
    const depegged = rate != null && Math.abs(rate - 1) > DOLLAR_PEG_TOLERANCE;
    return { symbol: dollarCoin.symbol, isDollar: !depegged };
  }
  const listed = registry?.get(address.trim().toLowerCase());
  return {
    symbol:
      displaySymbol(listed?.tokenSymbol, listed?.symbol ?? null, address) ??
      truncateAddress(address.trim()),
    isDollar: false
  };
}

/**
 * What a holding is worth, in the currency it is actually stated in.
 *
 * On a `source: 'rowa'` row the served `valueUsd` is the vault's STRATEGY
 * CURRENCY amount under a USD name (confirmed by the owner, 2026-10-01), so
 * printing or summing it as dollars shows a euro vault as "$1,000" and mixes
 * currencies in every total. `nativeValue` and `nativeCurrency` state the
 * same amount honestly, and `fx.rates` carries the conversion.
 */
interface HoldingValue {
  /** The amount in the row's own currency. */
  amount: number;
  /**
   * What that amount is counted in, for display: a symbol, or a shortened
   * address when nothing names the currency. Null when the response names
   * no currency for the row at all.
   */
  unit: string | null;
  /**
   * The amount is already a dollar figure: an external row (natively priced
   * in US dollars), or a vault whose currency is a dollar stablecoin for the
   * chain. It prints with a dollar sign and needs no second line.
   */
  isDollar: boolean;
  /**
   * The same amount in US dollars. Null when no exchange rate is served:
   * the row then stays out of every dollar total and comparison, and the
   * screen says so. Never assumed one for one.
   */
  usd: number | null;
  /** The conversion used a rate served past its freshness bound. */
  staleRate: boolean;
}

function finiteNumber(value: string | null | undefined): number | null {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The response holds a strategy currency that is not a dollar stablecoin for
 * its chain, so naming it takes the asset registry. `fx.rates` lists every
 * vault currency across all pages; the rows are checked too, for a response
 * that carries no fx block.
 */
export function needsCurrencyNames(
  response: InvestorHoldingsResponse
): boolean {
  const addresses = [
    ...(response.fx?.rates ?? []).map((rate) => rate.currency),
    ...response.holdings
      .filter((holding) => holding.source === 'rowa')
      .map((holding) => holding.nativeCurrency)
  ];
  // A depegged stablecoin is still named by `supportedTokens`, so only a
  // currency outside that set needs the registry.
  return addresses.some(
    (address) =>
      address != null && findDollarCoin(response.chainId, address) == null
  );
}

/** Null means unpriced: the position is real, its worth is not known. */
export function holdingValue(
  holding: InvestorHolding,
  fx: HoldingsFx,
  registry?: CurrencyRegistry
): HoldingValue | null {
  // The native fields describe a vault's strategy currency, so only a ROWA
  // row reads them. An external row's `valueUsd` is authoritative, and a
  // stray native pair on it must never convert it through a vault's rate.
  const native =
    holding.source === 'rowa' ? finiteNumber(holding.nativeValue) : null;
  if (native != null && holding.nativeCurrency) {
    const unit = currencyUnit(
      holding.chainId,
      holding.nativeCurrency,
      registry,
      fx
    );
    if (unit.isDollar) {
      return {
        amount: native,
        unit: unit.symbol,
        isDollar: true,
        usd: native,
        staleRate: false
      };
    }
    const served = servedRate(holding.nativeCurrency, fx);
    const rate = finiteNumber(served?.priceUsd);
    const usable = rate != null && rate > 0;
    return {
      amount: native,
      unit: unit.symbol,
      isDollar: false,
      usd: usable ? native * rate : null,
      staleRate: usable && served?.stale === true
    };
  }

  const served = finiteNumber(holding.valueUsd);
  if (served == null) return null;
  // External rows are natively priced in US dollars. A ROWA row without the
  // native fields is a strategy currency amount with no unit named.
  return holding.source === 'rowa'
    ? {
        amount: served,
        unit: null,
        isDollar: false,
        usd: null,
        staleRate: false
      }
    : {
        amount: served,
        unit: 'USD',
        isDollar: true,
        usd: served,
        staleRate: false
      };
}

export function formatHoldingValue(value: HoldingValue | null): string {
  if (value == null) return '—';
  return formatCompactMoney(
    value.amount,
    { isDollar: value.isDollar, unit: value.unit },
    { maximumFractionDigits: 2 }
  );
}

/**
 * A holdings response with every figure on one stated basis.
 *
 * The backend adds a ROWA row into `totals` and into `weightBps` at its
 * strategy currency amount (investor-holdings.service.ts: the weight is the
 * row's native value over a total that sums native values with dollars). So
 * beside rows this screen converts, the served total, the served weights,
 * "100%" and "reconciled" are a different basis from the amounts next to
 * them. This is the one place that decides which basis each screen states.
 *
 * - The whole wallet is in the response: its rows can be added up here, so
 *   the total is the sum of the converted rows and each share is the row's
 *   dollars over that sum. Nothing served is shown as a dollar fact.
 * - The wallet spans more than one page: the rows here cannot be added up
 *   to the wallet, so the served total and weights stand, together. They
 *   are only called reconciled when every vault across all pages (`fx.rates`
 *   lists each one's currency) is priced in a dollar stablecoin; otherwise
 *   a line says the total adds other currencies at their own amount.
 */
export interface HoldingsValuation {
  /** Each row's value in its own currency, null when unpriced. */
  values: Map<InvestorHolding, HoldingValue | null>;
  /**
   * Each row's share of the total below, in percent. Null for a row with no
   * dollar value. Drives donut arcs, dust folding and every share column.
   */
  shares: Map<InvestorHolding, number | null>;
  /**
   * What those shares are shares of, and every label says so:
   * - `nav`: the portfolio. The total is every holding at its dollar value.
   * - `valued`: what could be valued. A holding has no price or no rate,
   *   so the shares add up over the rest and are not of the portfolio.
   * - `page`: this page. The wallet spans more than one page and the served
   *   total adds another currency at its own amount, so the served weights
   *   (taken over that mixed total) are not used. Each row's share is its
   *   converted dollars over this page's valued subtotal instead: the only
   *   dollar denominator this response can state.
   */
  shareBasis: 'nav' | 'valued' | 'page';
  /** Every holding of the wallet is in this response. */
  isWholeWallet: boolean;
  /** The dollar values of the rows in this response, added up. */
  listedUsd: number;
  /** The portfolio total to print, and what it is. */
  total: {
    usd: number;
    /** Added up here from converted rows, or the number as served. */
    basis: 'converted' | 'served';
    /**
     * Every holding is in it at its dollar value: none is left out for lack
     * of a rate, and none is missing a price. False drops the word
     * "reconciled" and the "100%" beside the total.
     */
    reconciled: boolean;
  };
  /** Priced rows here with no dollar value, for the note that names them. */
  leftOut: HoldingValue[];
  /**
   * Holdings with no price at all, so not in any total. Counted from the
   * rows when the whole wallet is here, from the unfiltered coverage block
   * (`holdingsTotal − pricedCount`) when it spans more than one page.
   */
  unpricedCount: number;
  /** A row here was converted at a rate served past its freshness bound. */
  usedStaleRate: boolean;
  /** The served total adds a vault in another currency at its own amount. */
  servedIsMixed: boolean;
  /**
   * The currency the served totals are stated in. US dollars today; read
   * from the response rather than assumed, so a served total in anything
   * else never prints with a dollar sign.
   */
  servedCurrency: string;
}

export function valueHoldings(
  response: InvestorHoldingsResponse,
  registry?: CurrencyRegistry
): HoldingsValuation {
  const rows = response.holdings;
  const values = new Map<InvestorHolding, HoldingValue | null>(
    rows.map((holding) => [
      holding,
      holdingValue(holding, response.fx, registry)
    ])
  );
  const priced = [...values.values()].filter(
    (value): value is HoldingValue => value != null
  );
  const listedUsd = priced.reduce((sum, value) => sum + (value.usd ?? 0), 0);
  const leftOut = priced.filter((value) => value.usd == null);
  // The whole wallet is on this page only when it is the first page AND it
  // holds as many rows as the wallet has. `total` cannot say so: with
  // `?kind=` it counts the filtered rows, so a filtered page would read as
  // complete. `coverage.holdingsTotal` is computed over the unfiltered set.
  const isWholeWallet =
    response.page === 1 && response.coverage.holdingsTotal <= rows.length;
  // A holding with no price is real and is worth something nobody knows.
  // It is in no total, and a total beside it is not the whole portfolio.
  const unpricedCount = isWholeWallet
    ? rows.length - priced.length
    : Math.max(
        0,
        response.coverage.holdingsTotal - response.coverage.pricedCount
      );

  // `fx.rates` is built from the unfiltered ledger, so it names the currency
  // of every vault on every page. The rows on this page are checked too, for
  // a response that carries no fx block.
  const isOtherCurrency = (currency: string | null | undefined) =>
    currency != null &&
    currency !== '' &&
    !currencyUnit(response.chainId, currency, registry, response.fx).isDollar;
  const servedIsMixed =
    (response.fx?.rates ?? []).some((rate) => isOtherCurrency(rate.currency)) ||
    rows.some(
      (holding) =>
        holding.source === 'rowa' &&
        holding.valueUsd != null &&
        (holding.nativeCurrency == null ||
          isOtherCurrency(holding.nativeCurrency))
    );

  const reconciled = isWholeWallet
    ? leftOut.length === 0 && unpricedCount === 0
    : !servedIsMixed && unpricedCount === 0;
  // The served weights are the backend's native amounts over its covered
  // total. Across pages they are the only share of the whole there is, but
  // only while every vault is in a dollar stablecoin; once another currency
  // is added in unconverted they would size a converted row by its foreign
  // amount (100 EUR at $2 beside $100 read 50% and 50%).
  const usesServedWeights = !isWholeWallet && !servedIsMixed;
  const shareBasis: HoldingsValuation['shareBasis'] = reconciled
    ? 'nav'
    : isWholeWallet || usesServedWeights
      ? 'valued'
      : 'page';

  const shares = new Map<InvestorHolding, number | null>(
    rows.map((holding) => {
      const usd = values.get(holding)?.usd ?? null;
      if (usd == null) return [holding, null];
      if (usesServedWeights) {
        return [
          holding,
          holding.weightBps == null ? null : holding.weightBps / 100
        ];
      }
      return [holding, listedUsd > 0 ? (usd / listedUsd) * 100 : null];
    })
  );

  return {
    values,
    shares,
    shareBasis,
    isWholeWallet,
    listedUsd,
    total: isWholeWallet
      ? { usd: listedUsd, basis: 'converted', reconciled }
      : {
          usd: finiteNumber(response.totals.coveredValueUsd) ?? 0,
          basis: 'served',
          reconciled
        },
    leftOut,
    unpricedCount,
    usedStaleRate: priced.some((value) => value.staleRate),
    servedIsMixed,
    servedCurrency: response.valuationCurrency
  };
}

/**
 * The total as the screen prints it, in the currency it is stated in. A sum
 * of converted rows that valued nothing, because every holding lacks a rate
 * or a price, is not a measured zero: it prints "—", never "$0".
 */
export function formatHoldingsTotal(valuation: HoldingsValuation): string {
  if (
    valuation.total.basis === 'converted' &&
    valuation.listedUsd === 0 &&
    (valuation.leftOut.length > 0 || valuation.unpricedCount > 0)
  ) {
    return '—';
  }
  // A converted total is dollars by construction. A served total is in the
  // response's `valuationCurrency`, an ISO code by contract (not a token
  // name), so "USD" there does mean dollars.
  const isDollar =
    valuation.total.basis === 'converted' ||
    valuation.servedCurrency.trim().toUpperCase() === 'USD';
  return formatCompactMoney(
    valuation.total.usd,
    { isDollar, unit: valuation.servedCurrency },
    { maximumFractionDigits: 2 }
  );
}

/**
 * What the reader needs to be told about the figures above, one plain
 * sentence each: rows left out for lack of a rate, holdings with no price at
 * all, a served total that adds other currencies in unconverted, and a stale
 * rate that was used.
 *
 * "Left out" is only said of a total added up here. A served total does
 * include those rows, at their own amount, and the line after says so.
 */
export function valuationNotes(valuation: HoldingsValuation): string[] {
  const notes: string[] = [];

  if (valuation.total.basis === 'converted' && valuation.leftOut.length > 0) {
    const units = [
      ...new Set(
        valuation.leftOut
          .map((value) => value.unit)
          .filter((unit): unit is string => unit != null)
      )
    ];
    const one = valuation.leftOut.length === 1;
    const subject = one
      ? '1 position is'
      : `${valuation.leftOut.length} positions are`;
    const reason =
      units.length === 0
        ? `${one ? 'its' : 'their'} currency is not stated`
        : `there is no exchange rate for ${units.join(', ')} yet`;
    notes.push(`${subject} left out of the dollar totals because ${reason}.`);
  }

  if (valuation.unpricedCount > 0) {
    notes.push(
      valuation.unpricedCount === 1
        ? '1 position has no price yet, so it is not in the dollar totals.'
        : `${valuation.unpricedCount} positions have no price yet, so they are not in the dollar totals.`
    );
  }

  if (valuation.total.basis === 'served' && valuation.servedIsMixed) {
    notes.push(
      'Vaults priced in another currency are added to this total at their own amount, not converted to dollars.'
    );
  }

  if (valuation.usedStaleRate) {
    notes.push(
      'An exchange rate used here is older than usual, so these dollar amounts may shift when a fresh one arrives.'
    );
  }

  return notes;
}

export function formatTokenUnits(holding: InvestorHolding): string {
  if (holding.decimals == null) return '—';
  try {
    // Base units stay a bigint to the printed figure: rounded half up on
    // the exact amount, never through a float.
    const raw = BigInt(holding.balanceRaw);
    const { decimals } = holding;
    // Compact above a million so test faucet balances never wall the UI.
    return raw >= 1_000_000n * 10n ** BigInt(decimals)
      ? formatCompactUnits(raw, decimals, { maximumFractionDigits: 2 })
      : formatUnitsFixed(raw, decimals, { maxFraction: 4 });
  } catch {
    return '—';
  }
}

/**
 * A holding's unit price in the currency it is actually quoted in.
 *
 * On a ROWA row `priceUsd` is the vault's share price in its STRATEGY
 * CURRENCY, carried under a USD name — so printing it with a dollar sign
 * states a conversion nobody performed. v1.73.0 serves the same number as
 * `nativePrice` alongside the currency it belongs to, which is what this
 * renders. External rows are natively USD-priced and keep the dollar form.
 *
 * The Positions card and the full positions table both print their unit
 * price through this one function, so the two can never disagree.
 */
export function formatHoldingUnitPrice(
  holding: InvestorHolding,
  registry?: CurrencyRegistry,
  fx?: HoldingsFx
): string {
  // ROWA rows only, as in `holdingValue`.
  const native = holding.source === 'rowa' ? holding.nativePrice : null;
  const currency = holding.nativeCurrency;
  if (native != null && currency) {
    const price = sharePriceFigure(native);
    if (price == null) return '—';
    // `nativeCurrency` is a contract address. A dollar stablecoin vault is
    // quoted in dollars; anything else by its name, or a shortened address.
    const unit = currencyUnit(holding.chainId, currency, registry, fx);
    return unit.isDollar
      ? formatDecimalDollars(price)
      : `${price} ${unit.symbol}`;
  }
  // A ROWA row that names no currency: the number is a strategy currency
  // price with its unit missing, so it prints bare, the same way
  // `holdingValue` treats the row's value.
  if (holding.source === 'rowa') {
    if (holding.priceUsd == null) return '—';
    return sharePriceFigure(holding.priceUsd) ?? '—';
  }
  return formatUnitPrice(holding.priceUsd);
}

/**
 * A vault share price as the vault page prints it (`formatRoundedUnits`:
 * six places, or six significant digits under 1, rounded half up on the
 * served digits and never through a float). Share prices sit near 1, so a
 * row's units times its price only reconciles with its value at that
 * precision: 501.4986 units at "$1.02" is $511.53, not the $512.93 beside
 * them (QA 2026-10-04). Null for anything that is not a plain decimal.
 */
function sharePriceFigure(price: string): string | null {
  if (!/^\d+(?:\.\d+)?$/.test(price)) return null;
  const places = price.split('.')[1]?.length ?? 0;
  return formatRoundedUnits(parseUnits(price, places), places);
}

/** Sub-dollar prices keep significant digits — "$0" is never a price. */
function formatUnitPrice(priceUsd: string | null): string {
  if (priceUsd == null) return '—';
  const price = Number(priceUsd);
  if (!Number.isFinite(price)) return '—';
  if (price >= 1) {
    return formatUsdFigure(priceUsd, { maximumFractionDigits: 2 });
  }
  return `$${formatDecimal(price, { maximumSignificantDigits: 3 })}`;
}

/** Known sources get a plain name; an unknown one is humanized, never raw. */
export function priceSourceLabel(holding: InvestorHolding): string {
  return holding.priceSource
    ? (PRICE_SOURCE_LABELS[holding.priceSource] ??
        humanizeSlug(holding.priceSource))
    : 'Priced';
}

/** The column heading for a share, on the basis the valuation states. */
export function shareColumnLabel(valuation: HoldingsValuation | null): string {
  switch (valuation?.shareBasis) {
    case 'valued':
      return '% of valued';
    case 'page':
      return '% of this page';
    default:
      return '% NAV';
  }
}

/**
 * What a share is a share of, after the percentage in the donut popover.
 * Nothing when it is the portfolio.
 */
export function shareSuffix(
  valuation: HoldingsValuation | null
): string | undefined {
  switch (valuation?.shareBasis) {
    case 'valued':
      return 'of valued holdings';
    case 'page':
      return 'of this page';
    default:
      return undefined;
  }
}
